import React, { useEffect, useRef, useState } from 'react';
import ModalShell from './ModalShell';
import ErrorBanner from './ErrorBanner';
import { ActivityStatus, activityStatusIcon } from './RecentActivitySidebar';
import { toneClasses } from '../lib/tones';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface ActivityDetail {
  status: ActivityStatus;
  statusLabel: string;
  createdAt?: string | Date;
  finishedAt?: string | Date;
  /**
   * Plain-text description, shown in its own card under the fields. When
   * provided (even empty), the card is shown; empty renders `noDescription`.
   */
  description?: string;
  /** Log output. When provided, the logs card is shown. Plain text, or nodes
   *  when a line needs more than text (the tenant folds each line's raw
   *  technical text under «Ver detalle técnico»). */
  logs?: React.ReactNode;
  /** If true, auto-scroll logs and show a blinking cursor. */
  liveLogs?: boolean;
  /** Arbitrary JSON-serialisable data shown in a collapsible panel. */
  metadata?: unknown;
  /** Who/what ran the activity — username or API-token name. */
  performedBy?: string;
}

/**
 * An extra card in the fields grid, after the built-in ones (status, date,
 * author). Lets a consumer show its own attributes with the same card instead
 * of re-drawing the modal.
 */
export interface ActivityDetailField {
  label: string;
  value: React.ReactNode;
  /** Material Symbols name shown before the label. */
  icon?: IconName;
  /** Takes the whole row — for long values such as ids. */
  fullWidth?: boolean;
}

export interface ActivityDetailLabels {
  loading?: string;
  status?: string;
  createdAt?: string;
  finishedAt?: string;
  logs?: string;
  awaitingLogs?: string;
  techData?: string;
  performedBy?: string;
  empty?: string;
  description?: string;
  noDescription?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
  title: string;
  subtitle?: string;
  detail: ActivityDetail | null;
  labels?: ActivityDetailLabels;
  /** Extra cards after the built-in ones; only rendered with a `detail`. */
  extraFields?: ActivityDetailField[];
  /** Load error, shown as a banner above the content (replaces `empty`). */
  error?: string;
  headerActions?: React.ReactNode;
  locale?: string;
  /** IANA timezone to render timestamps in. Omit → viewer's browser timezone. */
  timeZone?: string;
  zIndex?: string;
  /** Passed to `ModalShell` (default `'full'`); `'auto'` grows with the content. */
  size?: 'full' | 'auto';
}

const DEFAULT_LABELS: Required<ActivityDetailLabels> = {
  loading: 'Cargando información…',
  status: 'Estado',
  createdAt: 'Creada el',
  finishedAt: 'Finalizada',
  logs: 'Logs de ejecución',
  awaitingLogs: 'Esperando logs…',
  techData: 'Datos técnicos (JSON)',
  performedBy: 'Realizada por',
  empty: 'Sin datos',
  description: 'Descripción',
  noDescription: 'Sin descripción',
};

const CARD_CLASS =
  'p-4 rounded-xl bg-white dark:bg-surface-dark border border-border-subtle dark:border-border-subtle-dark';
const CARD_LABEL_CLASS =
  'text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2';
const SECTION_HEADING_CLASS =
  'text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-3';

// `running` and `info` share the `info` tone; the icon tells them apart.
const STATUS_ICON_COLOR: Record<ActivityStatus, string> = {
  success: toneClasses.success.icon,
  error: toneClasses.danger.icon,
  warning: toneClasses.warning.icon,
  info: toneClasses.info.icon,
  running: toneClasses.info.icon,
  neutral: toneClasses.neutral.icon,
};

function formatDateTime(ts: string | Date | undefined, locale: string, timeZone?: string) {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleString(locale, timeZone ? { timeZone } : undefined);
}

/**
 * Generic detail modal for activity/task entries.
 *
 * Standard component used by both admin and tenant so a single status card,
 * logs panel and metadata inspector are shared. Consumers pass a flat
 * `ActivityDetail` plus localised `labels`; they keep responsibility for
 * fetching data and (if relevant) streaming live updates.
 *
 * Anything a consumer shows beyond status/date/author goes through the same
 * cards: `extraFields` (attributes), `detail.description` (free text) and
 * `error` (load failure) — never a hand-drawn copy of this layout. All three
 * are optional, so a consumer that passes none renders exactly as before.
 */
export default function ActivityDetailModal({
  isOpen,
  onClose,
  loading = false,
  title,
  subtitle,
  detail,
  labels,
  extraFields,
  error,
  headerActions,
  locale = 'es-ES',
  timeZone,
  zIndex = 'z-[110]',
  size,
}: Props) {
  const l = { ...DEFAULT_LABELS, ...(labels ?? {}) };
  const [jsonExpanded, setJsonExpanded] = useState(true);
  const logsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (detail?.liveLogs && logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [detail?.logs, detail?.liveLogs]);

  const icon = detail ? activityStatusIcon(detail.status) : null;
  const iconColor = detail ? STATUS_ICON_COLOR[detail.status] : '';
  const primaryDate = detail?.finishedAt ?? detail?.createdAt;
  const primaryDateLabel = detail?.finishedAt ? l.finishedAt : l.createdAt;
  // Three columns once there are three cards sharing a row; a full-width card
  // takes its own row and does not count.
  const rowCards =
    2 + (detail?.performedBy ? 1 : 0) + (extraFields ?? []).filter((f) => !f.fullWidth).length;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      zIndex={zIndex}
      headerActions={headerActions}
      size={size}
    >
      <div className="space-y-6">
        {error ? <ErrorBanner message={error} className="!mb-0" /> : null}
        {loading && !detail ? (
          <div className="flex flex-col items-center py-12 gap-4">
            <Icon name="progress_activity" className="animate-spin !text-[48px] text-primary" />
            <p className="text-sm text-slate-400">{l.loading}</p>
          </div>
        ) : detail ? (
          <>
            <div
              className={`grid grid-cols-1 gap-4 ${
                rowCards >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
              }`}
            >
              <div className={CARD_CLASS}>
                <span className={CARD_LABEL_CLASS}>
                  <Icon name="info" className="!text-[14px]" />
                  {l.status}
                </span>
                <div className="flex items-center gap-2">
                  {icon ? (
                    <Icon name={icon.name} className={`!text-[20px] ${icon.className} ${iconColor}`} />
                  ) : null}
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    {detail.statusLabel}
                  </span>
                </div>
              </div>
              <div className={CARD_CLASS}>
                <span className={CARD_LABEL_CLASS}>
                  <Icon name="calendar_month" className="!text-[14px]" />
                  {primaryDateLabel}
                </span>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {formatDateTime(primaryDate, locale, timeZone)}
                </p>
              </div>
              {detail.performedBy ? (
                <div className={CARD_CLASS}>
                  <span className={CARD_LABEL_CLASS}>
                    <Icon name="person" className="!text-[14px]" />
                    {l.performedBy}
                  </span>
                  <p
                    className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate"
                    title={detail.performedBy}
                  >
                    {detail.performedBy}
                  </p>
                </div>
              ) : null}
              {(extraFields ?? []).map((field, i) => (
                // `min-w-0` lets a `truncate` inside the value actually clip in the grid.
                <div
                  key={`${field.label}-${i}`}
                  className={`${CARD_CLASS} min-w-0 ${field.fullWidth ? 'col-span-full' : ''}`}
                >
                  <span className={CARD_LABEL_CLASS}>
                    {field.icon ? (
                      <Icon name={field.icon} className="!text-[14px]" aria-hidden="true" />
                    ) : null}
                    {field.label}
                  </span>
                  <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {field.value}
                  </div>
                </div>
              ))}
            </div>

            {detail.description !== undefined ? (
              <div>
                <h4 className={SECTION_HEADING_CLASS}>
                  <Icon name="notes" className="!text-[16px]" aria-hidden="true" />
                  {l.description}
                </h4>
                <p
                  className={`${CARD_CLASS} text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words`}
                >
                  {detail.description || l.noDescription}
                </p>
              </div>
            ) : null}

            {detail.logs !== undefined ? (
              <div>
                <h4 className={SECTION_HEADING_CLASS}>
                  <Icon name="terminal" className="!text-[16px]" />
                  {l.logs}
                </h4>
                <div
                  ref={logsRef}
                  className="bg-black/40 rounded-xl p-4 text-xs text-emerald-400/90 border border-white/5 leading-relaxed min-h-[150px] max-h-[250px] whitespace-pre-wrap overflow-x-auto overflow-y-auto"
                >
                  {detail.logs || l.awaitingLogs}
                  {detail.liveLogs ? (
                    <span className="inline-block w-2 h-4 bg-primary/50 ml-1 animate-pulse" />
                  ) : null}
                </div>
              </div>
            ) : null}

            {detail.metadata !== undefined && detail.metadata !== null ? (
              <div>
                <button
                  type="button"
                  onClick={() => setJsonExpanded((e) => !e)}
                  className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-slate-600 dark:hover:text-slate-200 transition-colors w-full mb-3"
                >
                  <Icon name="data_object" className="!text-[16px] text-purple-400" />
                  {l.techData}
                  <Icon name={jsonExpanded ? 'expand_less' : 'expand_more'} className="!text-[16px] ml-auto" />
                </button>
                {jsonExpanded ? (
                  <div className="bg-black/20 rounded-xl p-4 overflow-hidden border border-white/5">
                    <pre className="text-[10px] text-slate-400 overflow-x-auto">
                      {JSON.stringify(detail.metadata, null, 2)}
                    </pre>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : error ? null : (
          <p className="text-sm text-slate-400 text-center py-12">{l.empty}</p>
        )}
      </div>
    </ModalShell>
  );
}
